import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import { Star } from 'lucide-react'
import { LiveSessionsList, LiveSummaryStats } from '@/features/live/LiveSessionsList'
import { useTrainerDates } from '@/hooks/useDates'

function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 10 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
        />
      ))}
      <span className="ml-1.5 text-sm font-semibold text-gray-700">{rating}/10</span>
    </div>
  )
}

export default function TrainerFeedbackPage() {
  const { formatDate } = useTrainerDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['trainer-feedback-settings'],
    queryFn: () => adminApi.getTrainerFeedbackSettings().then(r => r.data),
  })

  const { data: feedbacks = [], isLoading: feedbackLoading } = useQuery({
    queryKey: ['trainer-feedback'],
    queryFn: () => adminApi.getTrainerFeedback().then(r => r.data),
    enabled: settings?.trainerVisible === true,
  })

  const { data: liveSessions = [] } = useQuery({
    queryKey: ['trainer-live-feedback'],
    queryFn: () => adminApi.getTrainerLiveInsights().then(r => r.data),
    enabled: settings?.trainerVisible === true,
  })

  if (settingsLoading) return <p className="text-gray-400 text-sm">Loading…</p>

  if (!settings?.trainerVisible) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Star size={32} className="mx-auto text-gray-200 mb-3" />
        <p className="text-sm">Feedback visibility is not enabled by your training company.</p>
      </div>
    )
  }

  const avgRating = feedbacks.length > 0
    ? (feedbacks.reduce((s, f) => s + f.rating, 0) / feedbacks.length).toFixed(1)
    : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Star size={22} className="text-amber-400 fill-amber-400" />
          My Feedback
        </h1>
        <p className="text-sm text-gray-500 mt-1">Client ratings for sessions you've delivered</p>
      </div>

      {feedbacks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-3xl font-bold text-amber-500">{avgRating}</p>
            <p className="text-xs text-gray-500 mt-1">Average rating</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center">
            <p className="text-3xl font-bold text-gray-900">{feedbacks.length}</p>
            <p className="text-xs text-gray-500 mt-1">Total responses</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm text-center col-span-2 sm:col-span-1">
            <p className="text-3xl font-bold text-green-600">
              {feedbacks.filter(f => f.rating >= 8).length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Rated 8+/10</p>
          </div>
        </div>
      )}

      {feedbackLoading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : feedbacks.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-400 bg-white border border-gray-200 rounded-xl">
          <Star size={28} className="mx-auto text-gray-200 mb-3" />
          <p>No feedback received yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {feedbacks.map(fb => (
            <div key={fb.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-2">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-semibold text-gray-900">{fb.courseName}</p>
                  <p className="text-sm text-gray-500">{fmtDate(fb.bookingDate)}</p>
                </div>
                <RatingStars rating={fb.rating} />
              </div>
              {fb.comment && (
                <p className="text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 whitespace-pre-wrap">
                  {fb.comment}
                </p>
              )}
              <p className="text-xs text-gray-400">
                Submitted {fmtDate(fb.submittedAt)}
              </p>
            </div>
          ))}
        </div>
      )}

      {liveSessions.length > 0 && (
        <div className="space-y-4 pt-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Live sessions</h2>
            <p className="text-sm text-gray-500 mt-1">Anonymous feedback from attendees in the live sessions you took part in</p>
          </div>
          <LiveSummaryStats sessions={liveSessions} />
          <LiveSessionsList sessions={liveSessions} formatDate={fmtDate} showClient={false} />
        </div>
      )}
    </div>
  )
}
